import type { AccountPhoneChannel } from '@/backend';
import { getCookie } from '@/shared/cookie';
import { createEventBus } from '@/shared/js';
import { BASE, WORKSPACE } from '../base/constants';

export const SETTINGS_STORAGE_KEY = 'settings';

export const SUPPORTED_LANGS = ['en', 'ar', 'ckb'] as const;

// Fallback channel used when sending a phone OTP for a user who has not yet
// picked one (e.g. an email-registered user binding a phone during KYC).
export const DEFAULT_OTP_CHANNEL: AccountPhoneChannel = 'whatsapp';

export type Settings = {
  displayQuote: 'usdt' | 'iqd';
  maskWallet: boolean;
  theme: 'dark' | 'light' | 'auto';
  language: (typeof SUPPORTED_LANGS)[number];
  terminalDesktopLayout: 'classic' | 'pro';
  phoneOtpChannel?: AccountPhoneChannel;
};

export const DEFAULT_SETTINGS: Settings = {
  displayQuote: 'usdt',
  maskWallet: false,
  theme: 'light',
  language: 'ar',
  terminalDesktopLayout: 'pro',
  phoneOtpChannel: undefined,
};

export const COOKIE_OPTIONS = {
  maxAge: 365 * 24 * 60 * 60,
  path: '/',
  sameSite: 'Lax' as const,
  secure: true,
};

// Keys changed locally but not yet confirmed by the server.
// Module-level so all useSettings instances share the same state.
export const pendingKeys = new Set<keyof Settings>();
export const pendingBus = createEventBus();

export const getLangFromUrl = (): Settings['language'] | null => {
  if (typeof window === 'undefined' || WORKSPACE !== 'public') return null;
  const parts = window.location.pathname.split('/').filter((x) => !!x);
  const lang = parts.find(
    (x, i) => SUPPORTED_LANGS.includes(x as never) && i > (BASE ? 0 : -1),
  );
  return (lang as Settings['language']) ?? null;
};

export const getLangFromOs = (): Settings['language'] | null => {
  if (typeof window === 'undefined') return null;
  const osLang = window.navigator.language?.split('-')[0]?.toLowerCase();
  return SUPPORTED_LANGS.includes(osLang as never)
    ? (osLang as Settings['language'])
    : null;
};

export const parseSettings = (
  raw: string | null | undefined,
): Partial<Settings> => {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Partial<Settings>;
  } catch {
    return {};
  }
};

export const mergeSettings = (
  cookieSettings: Partial<Settings>,
  serverSettings: Partial<Settings> | null,
): Settings => {
  const result = { ...DEFAULT_SETTINGS };
  const keys = Object.keys(DEFAULT_SETTINGS) as (keyof Settings)[];

  for (const key of keys) {
    if (key === 'language') continue;
    if (pendingKeys.has(key)) {
      result[key] = (cookieSettings[key] ??
        serverSettings?.[key] ??
        DEFAULT_SETTINGS[key]) as never;
    } else {
      result[key] = (serverSettings?.[key] ??
        cookieSettings[key] ??
        DEFAULT_SETTINGS[key]) as never;
    }
  }

  const urlLang = getLangFromUrl();
  if (urlLang) {
    result.language = urlLang;
  } else if (pendingKeys.has('language')) {
    result.language =
      cookieSettings.language ??
      serverSettings?.language ??
      getLangFromOs() ??
      DEFAULT_SETTINGS.language;
  } else {
    result.language =
      serverSettings?.language ??
      cookieSettings.language ??
      getLangFromOs() ??
      DEFAULT_SETTINGS.language;
  }

  return result;
};

export const getSettings = (): Settings =>
  mergeSettings(parseSettings(getCookie(SETTINGS_STORAGE_KEY)), null);
